import { useRive, useStateMachineInput } from '@rive-app/react-canvas';
import classNames from 'clsx';
import React, { type ChangeEvent, memo, useCallback, useContext, useEffect, useState } from 'react';
import { NestleFormWrapperContext } from 'reactApp/sections/Nestle/FormWrapper/FormWrapper.context';
import { NESTLE_HIDE_ON_EXPORT_CLASS } from 'reactApp/sections/Nestle/FormWrapper/FormWrapper.helpers';
import { animations, animationsMobile, backgroundImages, backgroundImagesMobile } from 'reactApp/sections/Nestle/PetsCard/Photo/Photo.data';

import styles from './Photo.css';

const RIVE_STATE_MACHINE_NAME = 'AVA State Machine ';
const RIVE_STATE_MACHINE_NAME_MOBILE = 'State Machine ava mobila';
const RIVE_TRIGGER_NAME = 'Trigger 1';

export const Photo = memo(() => {
    const [animationSrc, setAnimationSrc] = useState<string>('');
    const [animationSrcMobile, setAnimationSrcMobile] = useState<string>('');
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [isAnimated, setIsAnimated] = useState(false);

    const { formData, formErrors, setFormData, setFormErrors } = useContext(NestleFormWrapperContext);
    const { cardColor } = formData;

    const { rive, RiveComponent } = useRive({
        src: animationSrc,
        stateMachines: RIVE_STATE_MACHINE_NAME,
        autoplay: true,
    });

    const { rive: riveMobile, RiveComponent: RiveComponentMobile } = useRive({
        src: animationSrcMobile,
        stateMachines: RIVE_STATE_MACHINE_NAME_MOBILE,
        autoplay: true,
    });

    const triggerAnimation = useStateMachineInput(rive, RIVE_STATE_MACHINE_NAME, RIVE_TRIGGER_NAME);
    const triggerAnimationMobile = useStateMachineInput(riveMobile, RIVE_STATE_MACHINE_NAME_MOBILE, RIVE_TRIGGER_NAME);

    const backgroundImage = backgroundImages[cardColor];
    const backgroundImageMobile = backgroundImagesMobile[cardColor];

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];

            if (!file) {
                setError('попробуйте загрузить другое\u00A0фото');
                return;
            }

            const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                setError('загрузите файл в\u00A0формате .png, .jpg или .webp');
                return;
            }

            const maxSize = 15 * 1024 * 1024;
            if (file.size > maxSize) {
                setError('загрузите файл меньше\u00A015\u00A0МБ');
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
                setFormData((prev) => ({ ...prev, photo: true }));
                if (formErrors.photo) {
                    setFormErrors((prev) => ({ ...prev, photo: false }));
                }

                if (!isAnimated) {
                    triggerAnimation?.fire();
                    triggerAnimationMobile?.fire();
                    setTimeout(() => {
                        setIsAnimated(true);
                    }, 2000);
                }
            };
            reader.readAsDataURL(file);
        },
        [
            setError,
            setImagePreview,
            triggerAnimation,
            triggerAnimationMobile,
            setFormData,
            setFormErrors,
            formErrors.photo,
            isAnimated,
            setIsAnimated,
        ]
    );

    useEffect(() => {
        if (!isAnimated) {
            setAnimationSrc(animations[cardColor]);
            setAnimationSrcMobile(animationsMobile[cardColor]);
        }
    }, [cardColor, setAnimationSrc, setAnimationSrcMobile, isAnimated]);

    return (
        <div className={styles.root}>
            <div className={styles.photo}>
                <div className={styles.upload}>
                    <label className={styles.uploadText} htmlFor="fileInput">
                        {imagePreview ? (
                            <img src={imagePreview} className={styles.imagePreview} loading="eager" alt="Питомец" />
                        ) : error ? (
                            <span className={styles.error}>{error}</span>
                        ) : (
                            <span
                                className={classNames(styles.empty, {
                                    [styles.error]: formErrors.photo,
                                })}
                            >
                                нажмите, чтобы
                                <br />
                                загрузить фото питомца
                            </span>
                        )}
                    </label>
                    <input
                        id="fileInput"
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                        className={classNames(styles.hiddenInput, NESTLE_HIDE_ON_EXPORT_CLASS)}
                        onChange={handleChange}
                    />
                </div>
            </div>
            <div
                className={classNames(styles.animationWrapper, {
                    [styles.animation_animated]: isAnimated,
                })}
            >
                <img src={backgroundImage} alt="photo-background" className={styles.image} />
                <img src={backgroundImageMobile} alt="photo-background" className={styles.imageMobile} />
                <div className={styles.animationDesktop}>
                    <RiveComponent key={animationSrc} />
                </div>
                <div className={styles.animationMobile}>
                    <RiveComponentMobile key={animationSrcMobile} />
                </div>
            </div>
        </div>
    );
});

Photo.displayName = 'Photo';
